import { useCallback, useState } from "react"
import { format, isValid, parseISO } from "date-fns"
import {
	IconTrash2Outline18 as Trash2Icon,
	IconArrowTurnLeftOutline18 as UndoIcon,
} from "nucleo-ui-outline-18"

import { AlertDialog, Button, Dialog, IconButton } from "@productlane/ui"

import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useImportPortalId } from "../../import-portal"

interface DocsPreviewDialogProps {
	isOpen: boolean
	onOpenChange: () => void
	articles: ArticleImport[]
	onSuccess: () => void
}

export interface ArticleImport {
	title: string
	content?: string
	summary?: string
	imageUrl?: string
	dateIso?: string
	published?: boolean
	groupName?: string
}

function parseDate(dateString: string): Date | null {
	// Try parsing as ISO format first
	const isoDate = parseISO(dateString)
	if (isValid(isoDate)) {
		return isoDate
	}

	// Try parsing with native Date constructor (handles various formats)
	const nativeDate = new Date(dateString)
	if (isValid(nativeDate)) {
		return nativeDate
	}

	return null
}

function stripHtml(html: string): string {
	// Remove HTML tags
	const stripped = html.replace(/<[^>]*>/g, "")
	// Decode common HTML entities
	const decoded = stripped
		.replace(/&nbsp;/g, " ")
		.replace(/&amp;/g, "&")
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.replace(/&quot;/g, '"')
		.replace(/&#39;/g, "'")
	return decoded.trim()
}

export function DocsPreviewDialog({
	isOpen,
	onOpenChange,
	articles,
	onSuccess,
}: DocsPreviewDialogProps) {
	const [deleted, setDeleted] = useState<number[]>([])
	const [isConfirmCloseDialogOpen, setIsConfirmCloseDialogOpen] =
		useState(false)

	const portalInstanceId = useImportPortalId()

	const bulkCreateArticlesMutation =
		api.workspace.bulkCreateArticles.useMutation({
			onSuccess,
		})

	const handleDelete = useCallback(
		(i: number) => {
			if (deleted.includes(i)) {
				const newDeleted = deleted.filter((it) => it !== i)
				setDeleted(newDeleted)
			} else {
				setDeleted([...deleted, i])
			}
		},
		[deleted],
	)

	const handleUpload = useCallback(() => {
		const uploadArticles = articles.filter((_, i) => !deleted.includes(i))
		analytics.track("upload-articles-csv")
		bulkCreateArticlesMutation.mutate({
			articles: uploadArticles,
			portalInstanceId,
		})
	}, [articles, deleted, bulkCreateArticlesMutation, portalInstanceId])

	const handleOpenChange = useCallback(() => {
		// If upload is in progress, show confirmation dialog instead of closing
		if (bulkCreateArticlesMutation.isLoading) {
			setIsConfirmCloseDialogOpen(true)
		} else {
			onOpenChange()
		}
	}, [bulkCreateArticlesMutation.isLoading, onOpenChange])

	const handleConfirmClose = useCallback(() => {
		setIsConfirmCloseDialogOpen(false)
		onOpenChange()
	}, [onOpenChange])

	const handleCancelClose = useCallback(() => {
		setIsConfirmCloseDialogOpen(false)
	}, [])

	return (
		<>
			<AlertDialog
				isOpen={isConfirmCloseDialogOpen}
				onOpenChange={setIsConfirmCloseDialogOpen}
				title="Upload in progress"
				description="The docs upload is currently in progress. Are you sure you want to cancel and close?"
				confirmText="Stop & Close"
				cancelText="Continue Upload"
				onConfirm={handleConfirmClose}
				onCancel={handleCancelClose}
			/>

			<Dialog
				title="Preview"
				description="Please remove articles you might not want to upload"
				isOpen={isOpen}
				onOpenChange={handleOpenChange}
				footer={
					<div>
						<Button
							isLoading={bulkCreateArticlesMutation.isLoading}
							onClick={handleUpload}
						>
							Upload
						</Button>
					</div>
				}
			>
				<div className="space-y-2">
					{articles.map((article, i) => (
						<div
							key={`article-preview-${i}`}
							className="relative flex flex-col overflow-hidden rounded-lg border p-2.5 text-sm text-secondary"
						>
							<div className="flex items-center justify-between border-b pb-2">
								<div className="flex-1">
									<div className="font-medium text-primary">
										{article.title}
									</div>
									{article.groupName && (
										<div className="text-xs text-blue-600">
											Group: {article.groupName}
										</div>
									)}
									{article.dateIso &&
										(() => {
											const parsedDate = parseDate(article.dateIso)
											return parsedDate ? (
												<span className="text-xs">
													{format(parsedDate, "MMM d, yyyy")}
												</span>
											) : (
												<span className="text-xs text-red-600">
													Invalid date: {article.dateIso}
												</span>
											)
										})()}
									{article.published !== undefined && (
										<span
											className={`ml-2 text-xs ${article.published ? "text-green-600" : "text-gray-500"}`}
										>
											{article.published ? "Published" : "Unpublished"}
										</span>
									)}
								</div>
								<IconButton
									className="z-10"
									onClick={() => handleDelete(i)}
									variant="ghost"
									Icon={deleted.includes(i) ? UndoIcon : Trash2Icon}
								/>
							</div>
							{article.summary && (
								<div className="mt-2 line-clamp-2 text-xs text-tertiary">
									<strong>Summary:</strong> {stripHtml(article.summary)}
								</div>
							)}
							{article.content && (
								<div className="mt-2 line-clamp-3">
									{stripHtml(article.content)}
								</div>
							)}
							{article.imageUrl && (
								<div className="mt-1 text-xs text-blue-600">
									Image: {article.imageUrl}
								</div>
							)}

							{deleted.includes(i) && (
								<div className="absolute inset-0 z-1 bg-white/70 dark:bg-gray-900/70" />
							)}
						</div>
					))}
				</div>
			</Dialog>
		</>
	)
}
